import { GridColumnVisibilityModel, GridSortModel, GridColumns as XGridColumns } from '@mui/x-data-grid-pro';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { DrawerFitted } from 'app/components/Common/Drawer/Drawer';
import { SortColumn, gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import {
  selectFilterChipSelected,
  selectGridState,
} from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { CandidateDrawerPreviewer } from 'app/components/GlobalSearch/Candidate/CandidateDrawerPreviewer';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import _ from 'lodash';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { selectSearchResults } from 'store/redux-store/global-search/selectors';
import { globalSearchActions, totalCountErrorThreshold } from 'store/redux-store/global-search/slice';
import { RootState } from 'types';
import { candidateViewInfo } from './OrderCandidateMatchGridUtils';
import { getOrderCandidateMatchActions } from './store/orderCandidateMatch.redux';
import { selectAppliedFilters, selectOrderCandidateMatch } from './store/orderCandidateMatch.selectors';
import { formatMultiSortColumns, formatMultiSortXGridColumns } from '@AMIEWEB/GlobalSearch/helper';
import { useDecision } from '@optimizely/react-sdk';
import { ff_candidate_communication_grid } from 'app/constants/FeatureFlags/Tasks/Notifications/Keys';
import { ff_candidate_leads_management } from 'app/constants/FeatureFlags/Candidate/Keys';
import {
  getSelectedSkillSetFilter,
  getSkillSetFilter,
  ICandidateSkillSet,
} from 'app/ComponentLibrary/Filter/CandidateSkillset';
import { candidateSearchFilterName } from '@AMIEWEB/GlobalSearch/Candidate/SearchUtils';

export const orderCandidateMatchVisibiltyModel = {
  cid: true,
  candidate: true,
  firstName: false,
  lastName: false,
  goesBy: false,
  otherName: false,
  phones: false,
  emails: false,
  skillSet: true,
  unverifiedSkillsets: false,
  brand: true,
  candidateStatus: true,
  recruiter: true,
  recruitingSpecialist: true,
  activePlacement: true,
  licensed: true,
  availableToStartDate: true,
  lastContactedOnDate: true,
  callBackDate: true,
  applicationDate: true,
  city: true,
  state: true,
  lastPlacementEndDate: true,
  zip: true,
  radius: true,
  virtualInterview: true,
  covidVaxStatus: true,
};

const OrderCandidateMatchGrid = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const gridTag = 'OrderCandidateMatch';
  const [candidateCommunicationConsentFlag] = useDecision(ff_candidate_communication_grid);
  const [leadsManagementFlag] = useDecision(ff_candidate_leads_management);

  const [tableColumns, setTableColumns] = React.useState<any[]>(
    candidateViewInfo(t, candidateCommunicationConsentFlag.enabled, leadsManagementFlag?.enabled),
  );
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const { items: rows, count } = useSelector(selectOrderCandidateMatch);

  const { drawerData } = useSelector(selectSearchResults);

  const appliedFilter = useSelector(selectAppliedFilters);
  const filterChipSelected = useSelector(selectFilterChipSelected);

  const { promiseInProgress } = usePromiseTracker({ area: 'order-candidate-match-call', delay: 0 });

  const gridState = useSelector(selectGridState);
  const userPreference = useSelector(selectUserPreference);
  const gridPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === gridTag);
  });
  const defaultColumn = { column: 'candidateId', direction: 'desc' };
  const defaultSort = [{ field: 'candidateId', sort: 'desc' }];
  const [defaultSortModel] = React.useState<any>(defaultSort);
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(orderCandidateMatchVisibiltyModel),
  );
  const [defaultColumnVisibilityModel] = React.useState<any>(_.cloneDeep(orderCandidateMatchVisibiltyModel));
  const [defaultColumns] = React.useState<any>(
    candidateViewInfo(t, candidateCommunicationConsentFlag.enabled, leadsManagementFlag?.enabled),
  );
  const [currentColumns, setCurrentColumns] = React.useState<any>(
    candidateViewInfo(t, candidateCommunicationConsentFlag.enabled, leadsManagementFlag?.enabled),
  );

  const saveSortModel = React.useCallback(
    sortModel => {
      const sortedColumn =
        sortModel?.length > 0
          ? {
              column: sortModel[0].field,
              direction: sortModel[0].sort,
            }
          : undefined;
      const multiSortedColumns = sortModel?.length > 0 ? formatMultiSortColumns(sortModel) : [];
      if (
        !_.isEqual(sortedColumn, gridState?.sortedColumn) ||
        !_.isEqual(multiSortedColumns, gridState?.multiSortedColumns)
      ) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            sortedColumn: sortedColumn,
            multiSortedColumns: multiSortedColumns,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [dispatch, gridPreferences?.value, gridState?.sortedColumn, gridState?.multiSortedColumns],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      field?: string;
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!currentColumns || currentColumns.length === 0 || !userPreference.loaded) return;
      const currentGridColumns = new GridColumns(gridColumns).columns;

      if (currentGridColumns && currentGridColumns.length > 0 && currentGridColumns[0].field === '__check__')
        currentGridColumns.shift();

      const comparableColumns = new GridColumns(
        gridPreferences?.value?.columns,
        gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [currentColumns, userPreference.loaded, defaultColumns?.length, gridPreferences?.value, dispatch],
  );

  const getColumnPreferences = (cols: any[]) => {
    try {
      const columnPreferences = gridPreferences?.value;
      if (columnPreferences && columnPreferences.columns) {
        const newColumns = cols.map(column => {
          const foundIndex = columnPreferences.columns.findIndex(item => item?.field === column?.field);
          if (foundIndex !== -1) {
            return {
              ...column,
              ...columnPreferences.columns[foundIndex],
            };
          } else {
            return column;
          }
        });
        return newColumns.sort((a, b) => {
          return (
            columnPreferences.columns.findIndex(i => i.field === a.field) -
            columnPreferences.columns.findIndex(i => i.field === b.field)
          );
        });
      }
      return cols;
    } catch (err) {
      return cols;
    }
  };

  const getVisibilityPreferences = (cols: any) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences && columnPreferences?.columnVisibilityModel) {
      return columnPreferences?.columnVisibilityModel;
    }
    return cols;
  };

  const getSortModelPreferences = (sortModel: any[]) => {
    const columnPreferences = gridPreferences?.value;
    if (
      columnPreferences &&
      columnPreferences?.multiSortedColumns &&
      Array.isArray(columnPreferences?.multiSortedColumns)
    ) {
      return formatMultiSortXGridColumns(columnPreferences?.multiSortedColumns);
    }
    return sortModel;
  };

  const determineGridColumns = () => {
    const currentGridColumns = getColumnPreferences(defaultColumns);
    setCurrentColumns(currentGridColumns);
  };

  const determineVisibilityModel = () => {
    const currentVisibilityModel = getVisibilityPreferences(defaultColumnVisibilityModel);
    setCurrentColumnVisibilityModel(currentVisibilityModel);
  };
  const determineSortModel = () => {
    const currentGridSortModel = getSortModelPreferences(defaultSortModel);
    setCurrentSortModel(currentGridSortModel);
    if (
      !gridState?.sortedColumn ||
      (gridState?.sortedColumn &&
        (gridState.sortedColumn.column !== currentGridSortModel[0].field ||
          gridState.sortedColumn.direction !== currentGridSortModel[0].sort))
    ) {
      dispatch(
        gridStateActions.setSortedColumn({
          column: currentGridSortModel[0].field,
          direction: currentGridSortModel[0].sort,
        }),
      );
      if (gridState?.multiSortedColumns?.length > 0) {
        dispatch(gridStateActions.setMultiSortedColumn(gridState?.multiSortedColumns));
      }
    }
  };

  React.useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(gridTag));
      determineGridColumns();
      determineVisibilityModel();
      determineSortModel();
    }
  }, [userPreference?.loaded]);

  React.useEffect(() => {
    dispatch(gridStateActions.setGrid('CandidateMatch'));
    return () => {
      dispatch(
        gridStateActions.setSortedColumn({
          column: 'candidateId',
          direction: 'desc',
        }),
      );
      dispatch(
        gridStateActions.setMultiSortedColumn([
          {
            column: 'candidateId',
            direction: 'desc',
          },
        ]),
      );
    };
  }, []);

  const handleFetchData = async (
    pageSize: number,
    page: number,
    sortedColumn?: any,
    multiSortedColumns?: SortColumn[],
  ) => {
    //format the skillset from skillsetfilter
    const selectedSKillSetFilterChips = filterChipSelected?.[candidateSearchFilterName.candidateSkillSet];
    let skillsets: ICandidateSkillSet[] = [];
    if (selectedSKillSetFilterChips && selectedSKillSetFilterChips?.length > 0) {
      skillsets = getSelectedSkillSetFilter(
        appliedFilter?.skillsetFilter?.skillsets,
        selectedSKillSetFilterChips,
        candidateSearchFilterName.candidateSkillSet,
      );
    } else {
      skillsets = getSkillSetFilter(
        appliedFilter?.skillsetFilter?.skillsets,
        candidateSearchFilterName.candidateSkillSet,
      );
    }
    dispatch(
      getOrderCandidateMatchActions.getOrderCandidateMatchAction({
        pageSize: pageSize,
        pageNumber: page,
        translation: t,
        keyword: '',
        sortedColumn: sortedColumn || gridState.sortedColumn,
        multiSortedColumns: multiSortedColumns || gridState.multiSortedColumns,
        ...appliedFilter,
        //code: format the skillsetFilter as in onapply method
        skillsetFilter: {
          allowableMismatch: appliedFilter?.allowableMismatch || false,
          isVerifiedOnly: appliedFilter?.isVerifiedOnly || false,
          operation: appliedFilter?.operation || 'Or',
          isExactMatch: appliedFilter?.isExactMatch || false,
          skillsets: skillsets,
        },
      }),
    );
  };

  const handleResetColumns = async () => {
    setTableColumns(candidateViewInfo(t, candidateCommunicationConsentFlag.enabled, leadsManagementFlag?.enabled));
    setCurrentColumns(candidateViewInfo(t, candidateCommunicationConsentFlag.enabled, leadsManagementFlag?.enabled));
    setCurrentColumnVisibilityModel(orderCandidateMatchVisibiltyModel);
    dispatch(gridStateActions.setSortedColumn({ column: 'candidateId', direction: 'desc' }));
    dispatch(gridStateActions.setMultiSortedColumn([{ column: 'candidateId', direction: 'desc' }]));
    setCurrentSortModel(defaultSortModel);
    const preferenceData = {
      id: gridTag,
      value: {
        ...(gridPreferences?.value || {}),
        columns: defaultColumns,
        columnVisibilityModel: defaultColumnVisibilityModel,
        sortedColumn: { column: 'candidateId', direction: 'desc' },
        multiSortedColumns: [{ column: 'candidateId', direction: 'desc' }],
      },
    };
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    if (!(gridState?.sortedColumn?.column === 'candidateId' && gridState?.sortedColumn?.direction === 'desc')) {
      handleFetchData(gridState.pageSize, gridState.pageNumber, defaultColumn, []);
    }
  };

  const onSortModelChanged = React.useCallback(
    (model: GridSortModel) => {
      const pagination = apiRef?.current?.state?.pagination;
      const multiSortedColumns = model?.length > 0 ? formatMultiSortColumns(model) : [];
      if (pagination) {
        handleFetchData(
          pagination.pageSize,
          pagination.page + 1,
          model.length > 0
            ? {
                column: model[0].field,
                direction: model[0].sort,
              }
            : undefined,
          multiSortedColumns,
        );
      }
      if (model?.length > 0) {
        saveSortModel(model);
      }
      setCurrentSortModel(model);
    },
    [
      handleFetchData,
      gridState?.sortedColumn,
      gridState?.pageNumber,
      gridState?.pageSize,
      gridState?.multiSortedColumns,
    ],
  );

  const handleRefreshGrid = async () => {
    await handleFetchData(gridState.pageSize, gridState.pageNumber);
  };
  const onPageChange = (page: number) => {
    const pagination = apiRef?.current?.state?.pagination;
    if (pagination) handleFetchData(pagination?.pageSize ?? 0, page + 1);
  };

  const onPageSizeChange = (pageSize: number) => {
    if (gridState.pageSize !== pageSize) handleFetchData(pageSize, 1);
  };

  const handleGridChanges = () => {
    // setDisplayResetButton(false);
    const current = apiRef.current;
    const currentGridColumns = _.cloneDeep(current.getAllColumns());
    if (currentGridColumns && currentGridColumns.length > 0 && currentGridColumns[0].field === '__check__')
      currentGridColumns.shift();
    const fields = _.cloneDeep(current.state.columns.all);
    if (fields.length > 0 && fields[0] === '__check__') fields.shift();
  };

  return (
    <div>
      <XGrid
        apiRef={apiRef}
        columns={currentColumns}
        rowsPerPageOptions={[250, 300, 400, 500]}
        defaultRowsPerPage={250}
        defaultColumns={defaultColumns}
        defaultColumnVisibilityModel={defaultColumnVisibilityModel}
        columnVisibilityModel={currentColumnVisibilityModel}
        rows={rows || []}
        rowCount={count > totalCountErrorThreshold ? totalCountErrorThreshold : count}
        height={360}
        loading={promiseInProgress}
        disableMultipleColumnsSorting={false}
        onSortModelChange={onSortModelChanged}
        defaultSortModel={defaultSortModel}
        sortModel={currentSortModel}
        checkboxSelection
        onStateChange={handleGridChanges}
        gridTag={'CandidateMatch'}
        paginationMode="server"
        sortingMode="server"
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onColumnResizeStop={_columns => {
          savePreferences({
            field: undefined,
            columns: _columns,
            visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? defaultColumnVisibilityModel),
          });
        }}
        onColumnOrderChange={(params, event, details, _columns) => {
          savePreferences({
            field: params.field,
            columns: _columns,
            visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? defaultColumnVisibilityModel),
          });
        }}
        onColumnVisibilityModelChange={model => {
          dispatch(gridStateActions.setColumnVisibilityModel(model));
          setCurrentColumnVisibilityModel(_.cloneDeep(model));
          savePreferences({
            field: undefined,
            columns: _.cloneDeep(apiRef?.current?.getAllColumns()),
            visibilityModel: _.cloneDeep(model),
          });
        }}
        toolbarProps={{
          refreshButton: true,
          filterButton: true,
          refreshGrid: () => {
            handleRefreshGrid();
          },
          resetColumns: () => {
            handleResetColumns();
            dispatch(
              globalActions.setSnackBar({
                message: 'Columns have been reset.',
                severity: 'success',
              }),
            );
          },
        }}
      />
      {drawerData?.open && (
        <DrawerFitted
          onDrawerClose={() => {
            dispatch(globalSearchActions.setDrawerData({ open: false, data: undefined }));
          }}
          width={400}
          top={0}
          backgroundColor={theme.palette.framework.system.whisper}
          open={drawerData?.open || false}
        >
          {drawerData?.data ? <CandidateDrawerPreviewer isSearchGrid={true} isWorkDesk={true} /> : null}
        </DrawerFitted>
      )}
    </div>
  );
};

export default OrderCandidateMatchGrid;
